import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { signUp } from "../../utils/authHelpers";

const CreateAcc = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSendCode = async () => {
    if (!email || !password || !confirmPassword) {
      setError("All fields are required.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await signUp(email, password);
      navigate("/verify", { state: { email } });
    } catch (err) {
      setError(
        (err && err.message) || "An error occurred during signup."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[#2F2D2D] text-white px-4 sm:px-6 relative">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 sm:top-6 sm:left-6 text-[#CBDF52] flex items-center z-10"
      >
        <span className="text-base sm:text-lg">← Back</span>
      </button>

      {/* Centered Form Container */}
      <div className="bg-[#2F2D2D] w-full max-w-[757px] rounded-lg flex flex-col items-center justify-center py-6 sm:py-8 px-4 sm:px-6 mx-4">
        {/* Header */}
        <h1 className="font-[Montserrat] font-bold text-2xl sm:text-[36px] leading-tight sm:leading-[57.6px] tracking-[-3%] text-white text-center">
          Enter your email for verification
        </h1>
        <p className="text-white text-center mt-2 text-sm sm:text-base px-2">
          Please enter your email address to receive a code for verification.
        </p>

        {/* Form */}
        <div className="mt-6 w-full max-w-[430px]">
          {/* Email Address */}
          <div className="flex flex-col w-full">
            <label className="text-[#65DEF6] text-sm sm:text-md font-semibold mb-1 text-left">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-[53px] px-[8px] py-[10px] rounded-lg border border-gray-500 bg-[#2F2F2F] text-white placeholder-gray-400 focus:border-[#CBDF52] focus:outline-none"
              placeholder="johndoe@xyz.com"
            />
          </div>

          {/* Enter Password */}
          <div className="flex flex-col w-full mt-4 relative">
            <label className="text-[#65DEF6] text-sm sm:text-md font-semibold mb-2">
              Enter Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-[53px] px-[8px] py-[10px] rounded-lg border border-gray-500 bg-[#2F2F2F] text-white placeholder-gray-400 focus:border-[#CBDF52] focus:outline-none pr-12"
                placeholder="Please create a password"
              />
              <button
                type="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((p) => !p)}
                className="absolute inset-y-0 right-3 flex items-center"
              >
                {showPassword ? (
                  <EyeOff size={20} color="rgba(255,255,255,0.7)" />
                ) : (
                  <Eye size={20} color="rgba(255,255,255,0.7)" />
                )}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="flex flex-col w-full mt-6 relative">
            <label className="text-[#65DEF6] text-sm sm:text-md font-semibold mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirm ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full h-[53px] px-[8px] py-[10px] rounded-lg border border-gray-500 bg-[#2F2F2F] text-white placeholder-gray-400 focus:border-[#CBDF52] focus:outline-none pr-12"
                placeholder="Re-enter password"
              />
              <button
                type="button"
                aria-label={
                  showConfirm
                    ? "Hide confirm password"
                    : "Show confirm password"
                }
                onClick={() => setShowConfirm((p) => !p)}
                className="absolute inset-y-0 right-3 flex items-center"
              >
                {showConfirm ? (
                  <EyeOff size={20} color="rgba(255,255,255,0.7)" />
                ) : (
                  <Eye size={20} color="rgba(255,255,255,0.7)" />
                )}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <p className="text-red-500 text-sm mt-2 break-words">
              {String(error)}
            </p>
          )}

          {/* Password Requirements */}
          <div className="mt-6 w-full text-[#D0D0D0] text-xs sm:text-sm">
            <p>Your password must contain –</p>
            <div className="mt-2 flex flex-col sm:flex-row sm:justify-between space-y-2 sm:space-y-0">
              <ul className="list-disc list-inside space-y-1">
                <li>Minimum of 8 characters</li>
                <li>At least one uppercase letter</li>
                <li>At least one lowercase letter</li>
              </ul>
              <ul className="list-disc list-inside space-y-1">
                <li>At least one number</li>
                <li>At least one special character</li>
              </ul>
            </div>
          </div>

          {/* Send Code Button */}
          <button
            onClick={handleSendCode}
            disabled={loading}
            className={`mt-6 w-full h-[53px] rounded-lg text-black font-semibold text-lg transition duration-300 ${
              loading ? "bg-gray-500" : "bg-[#CBDF52] hover:bg-[#B0C947]"
            }`}
          >
            {loading ? "Sending..." : "Send code"}
          </button>
        </div>
      </div>

      {/* Dog Image (Bottom Right) - Hidden on mobile to prevent overlap */}
      <img
        src="/assets/images/puppy-2.png"
        alt="Dog Illustration"
        className="absolute w-[230.63px] h-[190.79px] bottom-[50px] right-[50px] hidden sm:block"
      />
    </div>
  );
};

export default CreateAcc;
